import * as React from 'react'

import { cn } from '../cn'

type MaxWidth = 'content' | 'data' | 'narrow' | 'none'

const maxWidthClass: Record<MaxWidth, string> = {
  // 80rem / 1280px: the "app homepage" width used by Tasker.
  content: 'max-w-7xl',
  // 96rem / 1536px: wider data-heavy pages with tables or task rows.
  data: 'max-w-[96rem]',
  // 28rem / 448px: narrow single-column layouts (chat, onboarding).
  narrow: 'max-w-2xl',
  none: '',
}

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Constrain the inner content width and center it. Keeps outer padding edge-to-edge
   * so background / borders can span the full viewport.
   * - `content` (80rem): standard app homepage / dashboard width.
   * - `data` (96rem): wider tables, lists, and other data-heavy pages.
   * - `narrow` (28rem): chat / single-column surfaces.
   * - `none` (default): content fills the available width.
   */
  maxWidth?: MaxWidth
  /**
   * Vertical spacing between top-level children. Defaults to `default`
   * (Tailwind `space-y-6`) so every page picks up the standard rhythm
   * between the header, the toolbar / filter bar, and the body; no need
   * for callers to add `className="space-y-6"` themselves. Set `none`
   * for fully bespoke layouts that manage their own spacing.
   */
  spacing?: 'default' | 'tight' | 'none'
}

const spacingClass: Record<NonNullable<PageContainerProps['spacing']>, string> = {
  default: 'space-y-6',
  tight: 'space-y-3',
  none: '',
}

/**
 * Standard page content wrapper used across every Ordo app.
 * Padding scales with viewport: p-4 on mobile, p-6 on desktop.
 * Adds extra bottom padding on mobile to clear a bottom-nav if present.
 *
 * Vertical spacing between children defaults to `space-y-6` so the
 * header / toolbar / body have consistent rhythm across pages without
 * each page repeating `<div className="space-y-6">` wrappers. Set
 * `spacing="none"` for fully bespoke layouts.
 */
export function PageContainer({
  children,
  className,
  maxWidth = 'none',
  spacing = 'default',
  ...props
}: PageContainerProps) {
  const innerClass = maxWidthClass[maxWidth]
  const spacingCls = spacingClass[spacing]
  return (
    <div
      data-slot={innerClass ? undefined : 'page-container-content'}
      className={cn('p-4 md:p-6 pb-24 md:pb-6', !innerClass && spacingCls, className)}
      {...props}>
      {innerClass ? (
        <div data-slot="page-container-content" className={cn('mx-auto w-full', innerClass, spacingCls)}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  )
}
